/**
 * Phase 19 / Task 19.5 (DEC-325, DASH-05) — Excel / CSV / Print for a dashboard view.
 * A copy of web-dispatch features/dashboard/DashboardExport.tsx (the apps share no
 * local component module; web-shared resolves from the main checkout in worktrees).
 *
 * The files are built SERVER-side from the same response the page shows
 * (`?format=xlsx|csv&table=…` on the view's own endpoint), so an export holds exactly
 * the screen's rows. Excel holds every table; CSV holds the one picked here.
 *
 * Print uses the browser's own dialog ("Save as PDF" included, DEC-325 D-4); the
 * `@media print` rules in styles.css hide the app chrome and these controls.
 *
 * The file name is built here, not read from Content-Disposition: a cross-origin
 * fetch cannot read that header unless the API exposes it.
 */

import * as React from 'react';
import { toast } from 'sonner';
import { authedFetch, useApiOpts } from '@lodex-pro/web-shared';

export interface ExportTableOption {
  readonly name: string;
  readonly label: string;
}

const buttonStyle: React.CSSProperties = {
  minHeight: '40px',
  padding: '0 14px',
  borderRadius: 'var(--radius-pill)',
  border: '1px solid var(--color-border-primary)',
  backgroundColor: 'var(--color-bg-secondary)',
  color: 'var(--color-text-primary)',
  fontWeight: 500,
  cursor: 'pointer',
};

/** Mirrors the API's exportFilename: 'operations-2026-09-01-to-2026-09-07-drivers.csv'. */
export function exportFileName(
  slug: string,
  from: string,
  to: string,
  format: 'csv' | 'xlsx',
  table?: string,
): string {
  const period = from === to ? from : `${from}-to-${to}`;
  return `${slug}-${period}${table === undefined ? '' : `-${table}`}.${format}`;
}

export function DashboardExport({
  path,
  params,
  slug,
  tables,
}: {
  /** The view's endpoint, e.g. '/v1/dashboards/operations'. */
  readonly path: string;
  /** The exact query the page used (from, to, …), so the file matches the screen. */
  readonly params: Readonly<Record<string, string>>;
  readonly slug: string;
  /** CSV choices; the first is the view's default table. */
  readonly tables: readonly ExportTableOption[];
}): React.JSX.Element {
  const apiOpts = useApiOpts();
  const [table, setTable] = React.useState(tables[0]?.name ?? '');
  const [busy, setBusy] = React.useState<'csv' | 'xlsx' | null>(null);
  const from = params['from'] ?? '';
  const to = params['to'] ?? '';

  const download = async (format: 'csv' | 'xlsx'): Promise<void> => {
    setBusy(format);
    try {
      const qs = new URLSearchParams({
        ...params,
        format,
        ...(format === 'csv' ? { table } : {}),
      });
      const res = await authedFetch(apiOpts, `${path}?${qs.toString()}`);
      if (!res.ok) {
        toast.error(
          res.status === 403 ? 'Your role cannot export this view.' : 'Export failed. Try again.',
        );
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = exportFileName(slug, from, to, format, format === 'csv' ? table : undefined);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed. Check your connection and try again.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="no-print"
      data-testid="dashboard-export"
      style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}
    >
      <button
        type="button"
        style={buttonStyle}
        disabled={busy !== null}
        onClick={(): void => void download('xlsx')}
      >
        {busy === 'xlsx' ? 'Preparing…' : 'Excel'}
      </button>
      <label style={{ fontSize: '12px', color: 'var(--color-text-tertiary)' }}>
        <span className="sr-only">CSV table</span>
        <select
          aria-label="CSV table"
          value={table}
          onChange={(e): void => setTable(e.target.value)}
          style={{ ...buttonStyle, cursor: 'default' }}
        >
          {tables.map((t) => (
            <option key={t.name} value={t.name}>
              {t.label}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        style={buttonStyle}
        disabled={busy !== null}
        onClick={(): void => void download('csv')}
      >
        {busy === 'csv' ? 'Preparing…' : 'CSV'}
      </button>
      <button type="button" style={buttonStyle} onClick={(): void => window.print()}>
        Print
      </button>
    </div>
  );
}
